import type { CreateInvitationBatch, InvitationBatchAction } from '@om/shared-types';

export interface InvitationBatchForm {
  name: string; templateId: number | null; purpose: CreateInvitationBatch['purpose'];
  eventIds: number[]; guestIds: number[]; requestRsvp: boolean;
  scheduledFor: string; rsvpDeadline: string; reminders: boolean;
  reminderIntervalDays: number; maxReminders: number; timeZone: string;
}

export const newInvitationBatchForm = (): InvitationBatchForm => ({
  name: '', templateId: null, purpose: 'invitation', eventIds: [], guestIds: [], requestRsvp: true,
  scheduledFor: '', rsvpDeadline: '', reminders: false, reminderIntervalDays: 3, maxReminders: 2,
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
});

/** Reject malformed dates and nonexistent local times (including DST gaps). */
export function localDateTimeToIso(value: string): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return undefined;
  const [, year, month, day, hour, minute] = match.map(Number);
  const date = new Date(year, month - 1, day, hour, minute);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day ||
      date.getHours() !== hour || date.getMinutes() !== minute) return undefined;
  return date.toISOString();
}

export function buildInvitationBatchInput(form: InvitationBatchForm, action: InvitationBatchAction,
  recipients: Array<{ contactId: number; email?: string }>, deadlineLimit?: string, now = Date.now()): CreateInvitationBatch {
  if (!form.name.trim()) throw new Error('Enter a batch name.');
  if (!form.templateId) throw new Error('Choose or create an email template.');
  if (!form.eventIds.length) throw new Error('Choose at least one event for this batch. Event scope never assigns guests.');
  if (!form.guestIds.length) throw new Error('Select at least one recipient.');
  if (form.guestIds.some(id => !recipients.find(row => row.contactId === id)?.email?.trim())) {
    throw new Error('Selected recipients without email cannot receive an email batch. Remove them or add their email in Guests. Personal links do not require email.');
  }
  const scheduledFor = form.scheduledFor ? localDateTimeToIso(form.scheduledFor) : undefined;
  if (action === 'schedule' && (!scheduledFor || Date.parse(scheduledFor) <= now)) {
    throw new Error('Choose a valid future local date and time to schedule.');
  }
  const rsvpDeadline = form.requestRsvp && form.rsvpDeadline ? localDateTimeToIso(form.rsvpDeadline) : undefined;
  if (form.requestRsvp && form.rsvpDeadline && (!rsvpDeadline || Date.parse(rsvpDeadline) <= now)) {
    throw new Error('Choose a valid future RSVP follow-up deadline.');
  }
  if (rsvpDeadline && deadlineLimit && Date.parse(rsvpDeadline) > Date.parse(deadlineLimit)) {
    throw new Error('The RSVP follow-up deadline cannot extend the existing event or celebration RSVP deadline.');
  }
  if (action === 'schedule' && rsvpDeadline && scheduledFor && Date.parse(rsvpDeadline) <= Date.parse(scheduledFor)) {
    throw new Error('The RSVP follow-up deadline must be after the scheduled send time.');
  }
  if (form.reminders && (!form.requestRsvp || !rsvpDeadline)) throw new Error('Reminders require an RSVP request and follow-up deadline.');
  if (form.reminders && (!Number.isInteger(form.reminderIntervalDays) || form.reminderIntervalDays < 1 || form.reminderIntervalDays > 30 ||
    !Number.isInteger(form.maxReminders) || form.maxReminders < 1 || form.maxReminders > 5)) {
    throw new Error('Reminder interval must be 1–30 days and maximum reminders must be 1–5.');
  }
  return {
    name: form.name.trim(), templateId: form.templateId, purpose: form.purpose,
    eventIds: form.eventIds, guestIds: [...new Set(form.guestIds)], requestRsvp: form.requestRsvp,
    timeZone: form.timeZone, action, ...(action === 'schedule' ? { scheduledFor } : {}),
    ...(rsvpDeadline ? { rsvpDeadline } : {}),
    ...(form.reminders ? { reminderIntervalDays: form.reminderIntervalDays, maxReminders: form.maxReminders } : {}),
  };
}